/**
 * ServiceRatingModal — one-time rating + feedback for a completed service
 * request, shown as a modal that auto-opens when the request reaches
 * `service_request_complete` and has not yet been rated.
 *
 * UX (modelled on the marineideas feedback form, rebuilt in the FinMin design
 * system): 1–5 hover stars with a rating label, FinMin-specific sentiment chips
 * that change per rating, an optional comment box, and Skip / Submit. The modal
 * gates nothing — the customer can dismiss it (Skip / outside-click / Esc) and
 * keep using the workspace.
 *
 * The backend rate endpoint stores a single free-text `feedback` field (no
 * structured tags), so the selected chips are folded into that string as a
 * comma-separated prefix, optionally followed by " — <comment>".
 */
import * as React from 'react';
import { Star, CheckCircle2 } from 'lucide-react';
import {
  Button,
  toast,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@finmin/ui';
import {
  useRateServiceRequest,
  type CustomerServiceRequestResponse,
} from '@/api/service-requests';
import { getServiceMeta } from '@/features/dashboard/constants';

// ── Copy ────────────────────────────────────────────────────────────────────

const RATING_LABELS: Record<number, string> = {
  1: 'Poor experience',
  2: 'Needs improvement',
  3: 'It was okay',
  4: 'Good experience',
  5: 'Excellent',
};

/** FinMin-specific sentiment chips, authored for the tax-service context. */
const FINMIN_RATING_TAGS: Record<number, string[]> = {
  1: [
    'Took longer than expected',
    'Communication was unclear',
    'Documents mishandled',
    'Did not resolve my issue',
    'Hard to reach the expert',
    'Other',
  ],
  2: [
    'Slower than I’d like',
    'Some steps were confusing',
    'Limited updates on progress',
    'Expert response was delayed',
    'Other',
  ],
  3: [
    'Got the job done',
    'A few delays along the way',
    'Communication was okay',
    'Wanted clearer guidance',
    'Other',
  ],
  4: [
    'Handled professionally',
    'Clear communication',
    'Reasonable turnaround',
    'Helpful expert',
    'Other',
  ],
  5: [
    'Excellent, thorough service',
    'Expert was very responsive',
    'Made taxes effortless',
    'Fast turnaround',
    'Would recommend FinminOS',
    'Other',
  ],
};

const COMMENT_MAX = 1000;

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  request: CustomerServiceRequestResponse;
}

export function ServiceRatingModal({ request }: Props) {
  const existingRating = request.rating ?? 0;
  const serviceTitle = getServiceMeta(request.service_type).title;

  // Auto-open once for a completed request that hasn't been rated yet.
  const [open, setOpen] = React.useState(existingRating === 0);

  const [rating, setRating] = React.useState(0);
  const [hover, setHover] = React.useState(0);
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const [comment, setComment] = React.useState('');
  // Flip to the in-modal thank-you immediately after a successful submit
  // (or a 409) without waiting on the refetched detail query.
  const [justRated, setJustRated] = React.useState(0);

  const { mutate, isPending } = useRateServiceRequest({
    onSuccess: (data) => {
      setJustRated(data.rating ?? rating);
      toast.success('Thanks for your feedback!');
    },
    onError: (err) => {
      // 409 = already rated (e.g. submitted from another tab) — treat as done.
      if (err.status === 409) {
        setJustRated(rating);
        return;
      }
      // 403 = blocked by an active payment gate ("Payment must be completed…").
      if (err.status === 403) {
        toast.error(err.message ?? 'Please complete the payment to continue.');
        return;
      }
      toast.error(err.message ?? 'Could not submit your rating.');
    },
  });

  // Already rated before this session → never show the modal.
  if (existingRating > 0) return null;

  const active = hover || rating;
  const availableTags = rating > 0 ? FINMIN_RATING_TAGS[rating] ?? [] : [];
  const submitted = justRated > 0;

  function handleRatingChange(n: number) {
    setRating(n);
    // Reset chips when the rating changes so the user doesn't carry over chips
    // that no longer appear in the new list.
    setSelectedTags([]);
  }

  function toggleTag(tag: string) {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  }

  function handleSubmit() {
    if (rating === 0 || isPending) return;
    const tagPart = selectedTags.join(', ');
    const commentPart = comment.trim();
    const feedback = [tagPart, commentPart].filter(Boolean).join(' — ');
    mutate({ id: request.id, payload: feedback ? { rating, feedback } : { rating } });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Block close while the request is in flight.
        if (isPending) return;
        setOpen(next);
      }}
    >
      <DialogContent
        className="max-w-md"
        // Don't let Radix auto-focus the first star on open — that paints a
        // focus ring on a star and reads as a pre-selected rating.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {submitted ? (
          // ── In-modal thank-you ───────────────────────────────────────────
          <div className="flex flex-col items-center text-center gap-3 py-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2 className="h-7 w-7 text-primary" aria-hidden="true" />
            </div>
            <DialogHeader className="space-y-1">
              <DialogTitle className="text-center">Thank you!</DialogTitle>
              <DialogDescription className="text-center">
                Your feedback on your {serviceTitle} helps us make FinminOS better
                for everyone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-1 mt-1" aria-label={`You rated ${justRated} out of 5`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={
                    n <= justRated
                      ? 'h-6 w-6 fill-accent text-accent'
                      : 'h-6 w-6 text-muted-foreground'
                  }
                  aria-hidden="true"
                />
              ))}
            </div>
            <Button
              type="button"
              variant="default"
              size="sm"
              className="mt-2"
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          </div>
        ) : (
          // ── Rating form ──────────────────────────────────────────────────
          <>
            <DialogHeader>
              <DialogTitle>Your {serviceTitle} is all done</DialogTitle>
              <DialogDescription>
                How was your experience? Your feedback helps us improve!
              </DialogDescription>
            </DialogHeader>

            {/* Stars */}
            <div className="mt-1">
              <div
                className="flex items-center justify-center gap-1.5"
                onMouseLeave={() => setHover(0)}
                role="radiogroup"
                aria-label="Rating"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={rating === n}
                    aria-label={`${n} star${n > 1 ? 's' : ''}`}
                    onClick={() => handleRatingChange(n)}
                    onMouseEnter={() => setHover(n)}
                    disabled={isPending}
                    className="p-0.5 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded disabled:pointer-events-none"
                  >
                    <Star
                      className={
                        n <= active
                          ? 'h-8 w-8 fill-accent text-accent transition-colors'
                          : 'h-8 w-8 text-muted-foreground transition-colors'
                      }
                      aria-hidden="true"
                    />
                  </button>
                ))}
              </div>
              {active > 0 && (
                <p className="text-xs font-medium text-muted-foreground mt-1.5 text-center">
                  {RATING_LABELS[active]}
                </p>
              )}
            </div>

            {/* Chips — only after a rating is chosen */}
            {rating > 0 && availableTags.length > 0 && (
              <div className="mt-1">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  What contributed to your rating?
                </p>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map((tag) => {
                    const selected = selectedTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        aria-pressed={selected}
                        disabled={isPending}
                        className={
                          'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-60 ' +
                          (selected
                            ? 'bg-accent text-accent-foreground border-accent'
                            : 'bg-background text-foreground border-border hover:bg-foreground/[0.04]')
                        }
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Comment */}
            <div>
              <label htmlFor="service-rating-comment" className="sr-only">
                Additional details
              </label>
              <textarea
                id="service-rating-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
                maxLength={COMMENT_MAX}
                rows={3}
                disabled={isPending}
                placeholder="Additional details"
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none leading-relaxed disabled:opacity-60"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={isPending}
              >
                Skip
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleSubmit}
                disabled={rating === 0 || isPending}
              >
                {isPending ? 'Saving…' : 'Submit rating'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
